import { useLayoutEffect, useState } from 'react';
import { ExclamationCircleIcon } from '@heroicons/react/24/outline';
import { Tooltip } from '@odo/shared-ui';
import { TOOLTIP_OVER_MODAL } from './DmControlPrimitives';

const DM_PREFIX = 'DM:';

// Re-measures on resize; the element arrives through a callback ref because the tooltip trigger
// remounts it whenever the tooltip switches on or off.
function useIsTruncated(element: HTMLElement | null, text: string | null): boolean {
    const [truncated, setTruncated] = useState(false);
    useLayoutEffect(() => {
        if (!element) {
            setTruncated(false);
            return;
        }
        const check = () => setTruncated(element.scrollWidth > element.clientWidth);
        check();
        const observer = new ResizeObserver(check);
        observer.observe(element);
        return () => observer.disconnect();
    }, [element, text]);
    return truncated;
}

interface OneLineNoticeProps {
    message: string | null;
    /** A refusal from Dental Monitoring: the icon and the "DM:" prefix. */
    fromDm?: boolean;
    className?: string;
}

// An error kept to one line so it never changes the layout; cut off, it shows in full in a
// tooltip that is reachable by keyboard too.
export function OneLineNotice({ message, fromDm = false, className = '' }: OneLineNoticeProps) {
    const [textElement, setTextElement] = useState<HTMLSpanElement | null>(null);
    const truncated = useIsTruncated(textElement, message);
    const full = message && (fromDm ? `${DM_PREFIX} ${message}` : message);
    return (
        <div className={`h-5 ${className}`} aria-live="polite">
            {message && (
                <Tooltip
                    content={full}
                    className={TOOLTIP_OVER_MODAL}
                    focusable
                    disabled={!truncated}
                >
                    <p className="flex min-w-0 items-center gap-1.5 rounded text-xs text-red-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500">
                        {fromDm && <ExclamationCircleIcon className="h-4 w-4 shrink-0" />}
                        <span ref={setTextElement} className="min-w-0 truncate">
                            {fromDm && <span className="font-semibold">{DM_PREFIX}</span>} {message}
                        </span>
                    </p>
                </Tooltip>
            )}
        </div>
    );
}
